import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import * as React from 'react';

import { Input } from '../../components/Input';
import { SubmitButton } from '../../components/SubmitButton';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';
import { date, time24hr, required2 } from '../../validators';
import { ToggleSwitch } from '../../components/ToggleSwitch';
import { RadioChoice } from '../../components/RadioChoice';
import { TextButton } from '../../components/ClickableText';
export interface MatchFields {
  atHome: string;
  excludedFromStats: 'yes' | 'no';
  matchDate: string;
  meetTime: string;
  matchTime: string;
  opponent: string;
  teamId: string;
  timeZone: string;
}

interface Props {
  initialValues: MatchFields;
  onSubmit: FormikConfig<MatchFields>['onSubmit'];
  submitText: string;
  loading?: boolean;
}

const validate = makeValidator<MatchFields>({
  atHome: [required2()],
  matchDate: [required2('Enter a date for the match'), date],
  meetTime: [time24hr],
  matchTime: [required2('Enter a time for the match'), time24hr],
  opponent: [required2('Enter the match opposition')],
  timeZone: [required2('Enter a time zone')],
});

const LazyTimeZoneInput = React.lazy(() =>
  import('../../components/TimeZoneInput').then(({ TimeZoneInput }) => ({
    default: TimeZoneInput,
  })),
);
const LazyTimeZoneDisplay = React.lazy(() =>
  import('../../components/TimeZoneDisplay').then(({ TimeZoneDisplay }) => ({
    default: TimeZoneDisplay,
  })),
);

export const MatchForm = ({
  initialValues,
  onSubmit,
  loading,
  submitText,
}: Props) => {
  const [timezoneInputVisible, setTimezoneInputVisible] = React.useState(false);
  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
    >
      {(formProps) => {
        const getInputProps = makeInputPropsMaker(formProps);
        const handleToggleChange =
          (field: keyof MatchFields) => (checked: boolean) => {
            formProps.setFieldValue(field, checked ? 'yes' : 'no');
          };

        const handleHomeToggle = (value: string) =>
          formProps.setFieldValue('atHome', value);

        return (
          <form
            noValidate={true}
            onSubmit={formProps.handleSubmit}
            className="space-y-6"
          >
            <Input label="Date" type="date" {...getInputProps('matchDate')} />

            <Input
              label="Meet time"
              type="time"
              {...getInputProps('meetTime')}
            />
            <div>
              <Input
                label="Match time"
                type="time"
                {...getInputProps('matchTime')}
              />
              {timezoneInputVisible ? (
                <div className="mt-2">
                  <React.Suspense fallback="...">
                    <LazyTimeZoneInput
                      value={formProps.values.timeZone}
                      onChange={(value) =>
                        formProps.setFieldValue('timeZone', value)
                      }
                    />
                  </React.Suspense>
                </div>
              ) : (
                <p className="text-xs text-grey-600 mt-2">
                  Match time zone:{' '}
                  <LazyTimeZoneDisplay timeZone={formProps.values.timeZone} />{' '}
                  <TextButton
                    type="button"
                    onClick={() => setTimezoneInputVisible(true)}
                  >
                    Change
                  </TextButton>
                </p>
              )}
            </div>
            <Input
              label="Opposition"
              type="text"
              {...getInputProps('opponent')}
            />

            <RadioChoice.Container
              value={formProps.values.atHome}
              onChange={handleHomeToggle}
            >
              <RadioChoice.Option value="yes">Home</RadioChoice.Option>
              <RadioChoice.Option value="no">Away</RadioChoice.Option>
            </RadioChoice.Container>

            <ToggleSwitch
              label="Friendly"
              description="If selected, votes, goals, and cards for this match will not
                count towards player leaderboards or individual stats"
              checked={formProps.values.excludedFromStats === 'yes'}
              onChange={handleToggleChange('excludedFromStats')}
              value={formProps.values.excludedFromStats}
            />

            <SubmitButton loading={loading}>{submitText}</SubmitButton>
          </form>
        );
      }}
    </Formik>
  );
};
